import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@/styles/videojs-theme.css';
import audioThumbnailDefault from '@/assets/audio-thumbnail.png';

interface LazyVideoPlayerProps {
  videoUrl: string;
  startTime?: number;
  endTime?: number;
  poster?: string;
  onTimeUpdate?: (currentTime: number) => void;
  isAudio?: boolean; // New prop to indicate if this is an audio file
}

/**
 * An optimized video player component that lazy loads content and only initializes when needed
 */
const LazyVideoPlayer = React.memo(({ 
  videoUrl, 
  startTime, 
  endTime, 
  poster, 
  onTimeUpdate,
  isAudio = false
}: LazyVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Determine the correct poster image
  const effectivePoster = isAudio ? audioThumbnailDefault : poster;
  
  // Only initialize player when component is visible
  useEffect(() => {
    if (!videoUrl || !videoRef.current || playerRef.current) return;
    
    let timeoutId: ReturnType<typeof setTimeout>;
    
    // Delay player initialization to improve perceived performance
    timeoutId = setTimeout(() => {
      if (!videoRef.current) return;
      
      const player = videojs(videoRef.current, {
        controls: true,
        preload: 'metadata', // Only load video metadata initially
        responsive: true,
        fluid: true,
        playbackRates: [0.5, 1, 1.5, 2],
        controlBar: {
          children: [
            'playToggle',
            'volumePanel',
            'currentTimeDisplay',
            'timeDivider',
            'durationDisplay',
            'progressControl',
            'playbackRateMenuButton',
            'fullscreenToggle',
          ],
        },
        html5: {
          hls: { overrideNative: true },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false
        }
      });
      
      playerRef.current = player;
      
      // Set initial time if provided
      if (startTime !== undefined) {
        player.currentTime(startTime);
      }
      
      // Handle loading states
      player.on('loadedmetadata', () => {
        setIsLoaded(true);
      });
      
      // Track play state
      player.on('play', () => {
        setIsPlaying(true);
      });
      
      player.on('pause', () => {
        setIsPlaying(false);
      });
      
      // Handle time updates
      if (onTimeUpdate) {
        player.on('timeupdate', () => {
          onTimeUpdate(player.currentTime());
        });
      }

      // Handle end time if provided
      if (endTime !== undefined) {
        player.on('timeupdate', () => {
          if (player.currentTime() >= endTime) {
            player.pause();
          }
        });
      }
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [videoUrl, startTime, endTime, onTimeUpdate]);
  
  // Show placeholder until video is ready
  return (
    <div data-vjs-player className="relative">
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-10">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-[#FF4A8E] border-t-transparent rounded-full animate-spin mb-2"></div>
            <span className="text-sm text-gray-600">Loading {isAudio ? 'audio' : 'video'}...</span>
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        className="video-js vjs-big-play-centered"
        poster={effectivePoster}
        preload="metadata"
      >
        <source src={videoUrl} type={isAudio ? 'audio/mp3' : 'video/mp4'} />
        <p className="vjs-no-js">
          To view this {isAudio ? 'audio' : 'video'} please enable JavaScript, and consider upgrading to a
          web browser that supports HTML5 {isAudio ? 'audio' : 'video'}
        </p>
      </video>
    </div>
  );
});

export default LazyVideoPlayer; 